import React, { useState, useEffect, useRef } from "react";
import {
  Checkbox,
  Button,
  TextField,
  Typography,
  IconButton,
  FormHelperText,
  makeStyles,
} from "@material-ui/core";

import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const useStyles = makeStyles((theme) => ({
  outlinedButtonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#0D47A1",
    height: "34px",
    textTransform: "none",
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#0D47A1",
    },
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  textFieldStyle: {
    marginBottom: 5,
    marginTop: 20,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 16,
    },
  },
  errorStyle: {
    marginLeft: 5,
    marginTop: 0,
    fontSize: 14,
    fontFamily: "Arial",
  },

  datePickerStyle: {
    width: "100%",
    marginBottom: 5,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 16,
    },
  },
}));

export default function AddEventToTermDialog({
  text,
  closeDialog,
  handleAddTerm,
  eventType,
  startDate,
  endDate,
  periodName
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [errorMsg, setErrorMsg] = useState({
    scheduledDate: "",
    eventName: "",
  });
  const [eventNameValue, setEventNameValue] = useState("");
  const [descValue, setDescValue] = useState("");
  const [attendanceRequired, setAttendanceRequired] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");

  const handleScheduledDateChange = (date) => {
    setErrorMsg({ scheduledDate: "" });
    setScheduledDate(date);
    if (!date)
      setErrorMsg({ scheduledDate: "Scheduled Date cannot be blank." });
  };
  const handleScheduledDateError = (error) => {
    if (error === "invalidDate")
      setErrorMsg({ scheduledDate: "Your date is not valid." });
  };
  function formatDate(dateString) {
    const options = { month: "short", day: "numeric" };
    const date = new Date(dateString);
    const dateFormatter = new Intl.DateTimeFormat("en-US", options);
    const formattedDate = dateFormatter.format(date);
    return `${formattedDate}`;
  }
  function dayFormat(dateObject) {
    const date = new Date(dateObject.$d);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const handleClose = () => {
    setOpen(false);
    closeDialog && closeDialog();
  };

  const handleSave = () => {
    if (eventNameValue.trim() === "") {
      setErrorMsg({ eventName: "Event Name cannot be blank." });
    } else if (errorMsg["scheduledDate"] === "Your date is not valid.") {
      return;
    } else if (!scheduledDate) {
      setErrorMsg({ scheduledDate: "Scheduled Date cannot be blank." });
    } else {
      setOpen(false);
      handleAddTerm(
        eventNameValue,
        eventType,
        formatDate(scheduledDate),
        dayFormat(scheduledDate),
        descValue,
        attendanceRequired
      );
    }
  };

  const handleEventNameChange = (event) => {
    if (event.target.value.trim() === "")
      setErrorMsg({ eventName: "Event Name cannot be blank." });
    else setErrorMsg({ eventName: "" });
    setEventNameValue(event.target.value);
  };
  const handleDescChange = (event) => {
    // Limit input to 254 characters
    if (event.target.value.length <= 254) {
      setDescValue(event.target.value);
    }
  };
  const minDate = dayjs(startDate);
  const maxDate = dayjs(endDate);
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "500px",
            },
          },
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="alert-dialog-title"
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingBottom: 0,
          }}
        >
          <Box mt={1} ml={1}>
            Add Event
          </Box>
          {closeDialog ? (
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                align: "right",
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Box
              pt={1}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography variant="h2">Period Name:</Typography>
              <Typography variant="h3" style={{ paddingLeft: 5 }}>{periodName}</Typography>
            </Box>
            <Box
              pt={1}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography variant="h2">Event Type:</Typography>
              <Typography variant="h3" style={{ paddingLeft: 5 }}>
                {eventType}
              </Typography>
            </Box>
            <TextField
              className={classes.textFieldStyle}
              fullWidth
              autoFocus
              required
              label="Event Name"
              margin="normal"
              name="eventName"
              variant="outlined"
              id="eventName"
              value={eventNameValue}
              onChange={handleEventNameChange}
            />
            {errorMsg && (
              <FormHelperText className={classes.errorStyle} error>
                {errorMsg.eventName}
              </FormHelperText>
            )}
            <TextField
              className={classes.textFieldStyle}
              fullWidth
              label="Description"
              margin="normal"
              name="Description"
              variant="outlined"
              id="Description"
              value={descValue}
              onChange={handleDescChange}
              multiline
              rows={4}
            />
            <Typography variant="h3">
              Attendance Required?{" "}
              <Checkbox
                onChange={(e) => setAttendanceRequired(e.target.checked)}
                id="Attendance Required?"
              />
            </Typography>
            <Box pt={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disablePast
                  className={classes.datePickerStyle}
                  label="Scheduled Date*"
                  id="scheduledDate"
                  name="scheduledDate"
                  onError={(newError) => handleScheduledDateError(newError)}
                  onChange={handleScheduledDateChange}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </LocalizationProvider>
              {errorMsg.scheduledDate && (
                <FormHelperText className={classes.errorStyle} error>
                  {errorMsg.scheduledDate}
                </FormHelperText>
              )}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Box mb={2}>
            <Button
              variant="contained"
              color="primary"
              className={classes.buttonStyle}
              style={{ marginRight: 5 }}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              variant="text"
              color="primary"
              className={classes.outlinedButtonStyle}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
}
